// Public launch page (/over). Static: no session, no API calls.
import { useState } from "react";
import { Link } from "react-router";

import { AdviceCard } from "../../components/AdviceCard";
import { buttonClass } from "../../components/Button";
import { CompanyAvatar } from "../../components/CompanyAvatar";
import { CompanyRow } from "../../components/CompanyRow";
import { Logo } from "../../components/icons";
import { StatusChip } from "../../components/StatusChip";
import { nl } from "../../i18n/nl";
import { PRIVACY_PATH } from "../../routes/screens";
import styles from "./LaunchPage.module.css";

const t = nl.launch;

function ProductPreview() {
  const companies = t.previewCompanies;
  const [selectedId, setSelectedId] = useState<string>(companies[1].id);
  const selected = companies.find((company) => company.id === selectedId) ?? companies[1];
  return (
    <figure className={styles.preview}>
      <div className={styles.previewBar}>
        <span>{t.previewForYou}</span>
        <span className={styles.previewProfile}>{t.previewProfile}</span>
      </div>
      <div className={styles.previewBody}>
        <div role="list" aria-label={t.previewList} className={styles.previewList}>
          {companies.map((company) => (
            <div role="listitem" key={company.id}>
              <CompanyRow
                company={{
                  id: company.id,
                  name: company.name,
                  status: company.status,
                  detail: company.place,
                  tag: company.advice,
                }}
                selected={company.id === selectedId}
                onSelect={setSelectedId}
              />
            </div>
          ))}
        </div>
        <div className={styles.previewDetail} aria-live="polite">
          <StatusChip status={selected.status} />
          <div className={styles.previewHeading}>
            <CompanyAvatar id={selected.id} name={selected.name} size={44} />
            <div>
              <p className={styles.previewName}>{selected.name}</p>
              <p className={styles.previewPlace}>
                {selected.place} · {t.previewDistance(selected.km)}
              </p>
            </div>
          </div>
          <AdviceCard
            status={selected.status}
            title={selected.title}
            text={selected.text}
            actions={
              <span className={buttonClass(selected.status === "nee" ? "secondary" : "primary")}>
                {selected.cta}
              </span>
            }
          />
        </div>
      </div>
      <figcaption className={styles.previewCaption}>{t.previewLabel}</figcaption>
    </figure>
  );
}

export function LaunchPage() {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/over" className={styles.brand}>
          <Logo />
          <span>{nl.app.name}</span>
        </Link>
        <nav aria-label={t.navLabel} className={styles.nav}>
          <a href="#hoe-het-werkt" className={styles.navLink}>
            {t.navHow}
          </a>
          <a href="#spelregels" className={styles.navLink}>
            {t.navRules}
          </a>
          <a href="#faq" className={styles.navLink}>
            {t.navFaq}
          </a>
          <Link to="/inloggen" className={buttonClass("secondary")}>
            {t.login}
          </Link>
        </nav>
      </header>

      <main id="inhoud">
        <section className={styles.hero} aria-labelledby="hero-title">
          <p className={styles.kicker}>{t.kicker}</p>
          <h1 id="hero-title" className={styles.heroTitle}>
            {t.heroTitle}
          </h1>
          <p className={styles.lead}>{t.heroLead}</p>
          <div className={styles.ctas}>
            <Link to="/registreren" className={buttonClass("primary", "large")}>
              {t.ctaRegister}
            </Link>
            <a href="#hoe-het-werkt" className={buttonClass("secondary", "large")}>
              {t.ctaHow}
            </a>
          </div>
          <p className={styles.note}>{t.heroNote}</p>
          <ProductPreview />
        </section>

        <section id="hoe-het-werkt" className={styles.section} aria-labelledby="how-title">
          <p className={styles.kicker}>{t.howKicker}</p>
          <h2 id="how-title" className={styles.sectionTitle}>
            {t.howTitle}
          </h2>
          <p className={styles.sectionLead}>{t.howLead}</p>
          <ol className={styles.steps}>
            {t.steps.map((step, index) => (
              <li key={step.title} className={styles.card}>
                <span className={styles.number}>{String(index + 1).padStart(2, "0")}</span>
                <h3 className={styles.cardTitle}>{step.title}</h3>
                <p className={styles.cardText}>{step.text}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className={styles.section} aria-labelledby="features-title">
          <h2 id="features-title" className={styles.sectionTitle}>
            {t.featuresTitle}
          </h2>
          <p className={styles.sectionLead}>{t.featuresLead}</p>
          <ul className={styles.features}>
            {t.features.map((feature) => (
              <li key={feature.title} className={styles.card}>
                <h3 className={styles.cardTitle}>{feature.title}</h3>
                <p className={styles.cardText}>{feature.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section id="spelregels" className={styles.section} aria-labelledby="rules-title">
          <p className={styles.kicker}>{t.rulesKicker}</p>
          <h2 id="rules-title" className={styles.sectionTitle}>
            {t.rulesTitle}
          </h2>
          <ol className={styles.rules}>
            {t.rules.map((rule, index) => (
              <li key={rule.title} className={styles.rule}>
                <span className={styles.number}>{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3 className={styles.cardTitle}>{rule.title}</h3>
                  <p className={styles.cardText}>{rule.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className={styles.section} aria-labelledby="privacy-title">
          <h2 id="privacy-title" className={styles.sectionTitle}>
            {t.privacyTitle}
          </h2>
          <div className={styles.privacy}>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>{t.privacyKeepTitle}</h3>
              <ul className={styles.plainList}>
                {t.privacyKeep.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
            <div className={styles.card}>
              <h3 className={styles.cardTitle}>{t.privacyNeverTitle}</h3>
              <ul className={styles.plainList}>
                {t.privacyNever.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className={styles.sectionLead}>
            {t.privacyNote} <Link to={PRIVACY_PATH}>{t.privacyMore}</Link>
          </p>
        </section>

        <section id="faq" className={styles.section} aria-labelledby="faq-title">
          <p className={styles.kicker}>{t.faqKicker}</p>
          <h2 id="faq-title" className={styles.sectionTitle}>
            {t.faqTitle}
          </h2>
          <p className={styles.sectionLead}>{t.faqLead}</p>
          <div className={styles.faq}>
            {t.faq.map((item, index) => (
              <details key={item.q} className={styles.faqItem} open={index === 0}>
                <summary className={styles.faqQuestion}>{item.q}</summary>
                <p className={styles.faqAnswer}>{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className={styles.final} aria-labelledby="final-title">
          <h2 id="final-title" className={styles.sectionTitle}>
            {t.finalTitle}
          </h2>
          <p className={styles.sectionLead}>{t.finalText}</p>
          <Link to="/registreren" className={buttonClass("primary", "large")}>
            {t.ctaRegister}
          </Link>
        </section>
      </main>

      <footer className={styles.footer}>
        <span>{t.footer}</span>
      </footer>
    </div>
  );
}
